import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import {
  Rocket, HeartPulse, Shield, Plane, TrendingUp, CloudRain, Leaf, Droplet,
  Fuel, Fingerprint, Radar, Trophy, BookOpen, Dice5, Network, Eye, Wifi, X, Save, RefreshCw, GitBranch, Handshake,
  Pencil, ExternalLink, Lightbulb, Check, ThumbsDown, History, BrainCircuit, Copy, Download, Square, CheckSquare,
  Sparkles, AudioLines,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { FLEET_CAPACITY } from "@/pages/Landing";

const CATEGORY_ICON = {
  health: HeartPulse, security: Shield, drone: Plane, finance: TrendingUp,
  weather: CloudRain, environment: Leaf, culture: BookOpen, "neural-ops": Network,
  identity: Fingerprint, vision: Eye, sports: Trophy, infrastructure: Wifi,
  energy: Fuel, diplomacy: Handshake, general: Rocket,
};

const CATEGORY_COLOR = {
  health: "#FF3366", security: "#00F0FF", drone: "#FF9F1C", finance: "#10B981",
  weather: "#8B5CF6", environment: "#10B981", culture: "#FF9F1C", "neural-ops": "#00F0FF",
  identity: "#FF3366", vision: "#8B5CF6", sports: "#FF9F1C", infrastructure: "#94A3B8",
  energy: "#FF3366", diplomacy: "#38BDF8", general: "#94A3B8",
};

const STATUS_LABEL = { awaiting: "Awaiting URL", docked: "Docked", error: "Signal Error", empty: "Under Construction" };

// Distinct realtime voices a ship can adopt (empty = inherit the fleet default, Marin).
const VOICE_OPTIONS = [
  { id: "", label: "Fleet default (Marin)" },
  { id: "marin", label: "Marin — warm, natural (recommended)" },
  { id: "cedar", label: "Cedar — warm, rounded (recommended)" },
  { id: "alloy", label: "Alloy — neutral, balanced" },
  { id: "ash", label: "Ash — measured, grounded" },
  { id: "ballad", label: "Ballad — expressive, lyrical" },
  { id: "coral", label: "Coral — bright, friendly" },
  { id: "echo", label: "Echo — calm, even" },
  { id: "sage", label: "Sage — soft, thoughtful" },
  { id: "shimmer", label: "Shimmer — light, airy" },
  { id: "verse", label: "Verse — energetic, dynamic" },
];

function ProjectCard({ project, onEdit, onSync, syncing, suggestions, onOpenSuggestions, onOpenHive, bridge }) {
  const Icon = CATEGORY_ICON[project.category] || Rocket;
  const color = CATEGORY_COLOR[project.category] || "#94A3B8";
  const isDocked = project.status === "docked";
  const canLaunch = isDocked && !!project.url;

  const handleCardClick = () => {
    if (canLaunch) {
      window.open(project.url, "_blank", "noopener,noreferrer");
    } else {
      onEdit(project);
    }
  };

  return (
    <motion.div
      data-testid={`project-card-${project.id}`}
      className="relative bg-[#0B0E14] border border-[#1A202C] rounded-xl p-4 flex flex-col gap-3 hover:border-white/20 transition-colors cursor-pointer"
      whileHover={{ y: -2 }}
      onClick={handleCardClick}
      title={canLaunch ? `Launch ${project.name}` : `Configure live URL for ${project.name}`}
    >
      {suggestions?.length > 0 && (
        <motion.button
          data-testid={`suggestion-badge-${project.id}`}
          onClick={(e) => { e.stopPropagation(); onOpenSuggestions(project); }}
          title={`AVA has ${suggestions.length} review-and-approve insight${suggestions.length > 1 ? "s" : ""} for ${project.name}`}
          className="absolute -top-2.5 -right-2.5 z-10 flex items-center gap-1 rounded-full bg-amber-400 text-black px-2 py-1 text-[10px] font-mono-data font-semibold shadow-[0_0_12px_rgba(255,159,28,0.6)]"
          animate={{ boxShadow: ["0 0 6px rgba(255,159,28,0.4)", "0 0 16px rgba(255,159,28,0.85)", "0 0 6px rgba(255,159,28,0.4)"] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        >
          <Lightbulb className="w-3 h-3" /> {suggestions.length}
        </motion.button>
      )}
      <div className="flex items-start justify-between">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}18`, border: `1px solid ${color}40` }}>
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
        <span
          className="text-[9px] font-mono-data uppercase tracking-wider px-2 py-1 rounded-full"
          style={{
            color: isDocked ? "#10B981" : project.status === "error" ? "#FF3366" : project.status === "empty" ? "#94A3B8" : "#94A3B8",
            backgroundColor: isDocked ? "#10B98118" : project.status === "error" ? "#FF336618" : project.status === "empty" ? "#94A3B818" : "#94A3B818",
          }}
          data-testid={`project-status-${project.id}`}
        >
          {STATUS_LABEL[project.status] || project.status}
        </span>
      </div>
      <div>
        <p className="font-display font-semibold text-sm text-white truncate">{project.name}</p>
        <p className="text-[10px] font-mono-data text-slate-500 truncate mt-0.5">{project.repo_name}</p>
        {canLaunch && (
          <p className="text-[9px] font-mono-data uppercase tracking-wider text-cyan-400/80 flex items-center gap-1 mt-1.5" data-testid={`launch-indicator-${project.id}`}>
            <ExternalLink className="w-2.5 h-2.5" /> Click card to launch live app
          </p>
        )}
        {bridge?.absorb_ok ? (
          <p
            className="text-[9px] font-mono-data uppercase tracking-wider text-emerald-400/90 flex items-center gap-1.5 mt-1.5"
            data-testid={`local-bridge-badge-${project.id}`}
            title="Local context live — AVA opens each voice session already knowing this ship's local people & recent conversations (ABSORB) and retains every spoken turn back into the ship (RETAIN)."
          >
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400" />
            </span>
            Local bridge live
          </p>
        ) : bridge?.summary_ok ? (
          <p
            className="text-[9px] font-mono-data uppercase tracking-wider text-slate-400/80 flex items-center gap-1.5 mt-1.5"
            data-testid={`data-bridge-badge-${project.id}`}
            title="Generic data bridge only — AVA can read this ship's domain data via the ship_data tool, but its local-context endpoints (people & conversations) aren't wired yet, so ABSORB/RETAIN is inactive here."
          >
            <span className="inline-flex rounded-full h-1.5 w-1.5 bg-slate-500" />
            Data bridge only
          </p>
        ) : (project.data_bridge_url && bridge) ? (
          <p
            className="text-[9px] font-mono-data uppercase tracking-wider text-amber-400/80 flex items-center gap-1.5 mt-1.5"
            data-testid={`bridge-down-badge-${project.id}`}
            title="A data bridge is registered for this ship but it isn't answering right now (stale registration, or the ship is asleep)."
          >
            <span className="inline-flex rounded-full h-1.5 w-1.5 bg-amber-500" />
            Bridge not answering
          </p>
        ) : null}
      </div>
      <div className="flex items-center justify-between mt-1 pt-2 border-t border-white/5">
        <span className="text-[9px] font-mono-data text-slate-500" data-testid={`project-concepts-${project.id}`}>
          {project.concepts_contributed || 0} concepts fed to brain
        </span>
        <div className="flex items-center gap-3">
          {project.repo_url && (
            <a
              data-testid={`project-repo-link-${project.id}`}
              href={project.repo_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-slate-500 hover:text-cyan-400 transition-colors"
              title="View source on GitHub"
            >
              <GitBranch className="w-3.5 h-3.5" />
            </a>
          )}
          <button
            data-testid={`hive-project-${project.id}`}
            onClick={(e) => { e.stopPropagation(); onOpenHive(project); }}
            className="text-slate-500 hover:text-emerald-400 transition-colors"
            title="Hive Mind integration - plug this ship into AVA's shared brain and voice"
          >
            <BrainCircuit className="w-3.5 h-3.5" />
          </button>
          <button
            data-testid={`edit-project-${project.id}`}
            onClick={(e) => { e.stopPropagation(); onEdit(project); }}
            className="text-slate-500 hover:text-amber-400 transition-colors"
            title="Edit live URL"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <button
            data-testid={`sync-project-${project.id}`}
            onClick={(e) => { e.stopPropagation(); onSync(project); }}
            disabled={syncing}
            className="text-slate-500 hover:text-cyan-400 transition-colors disabled:opacity-40"
            title="Sync from git"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>
    </motion.div>
  );
}

function EditModal({ project, onClose, onSaved }) {
  const [url, setUrl] = useState(project.url || "");
  const [apiKey, setApiKey] = useState(project.api_key || "");
  const [status, setStatus] = useState(project.status);
  const [voice, setVoice] = useState(project.voice || "");
  const [expertise, setExpertise] = useState(project.expertise || "");
  const [suggesting, setSuggesting] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSuggest = async () => {
    setSuggesting(true);
    try {
      const res = await api.post(`/projects/${project.id}/suggest-expertise`);
      setExpertise(res.data.expertise || "");
      toast.success("Drafted a specialist profile — edit it to taste, then Save");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't draft a profile right now");
    } finally {
      setSuggesting(false);
    }
  };

  const handleSave = async () => {
    const trimmed = url.trim();
    let normalizedUrl = "";
    if (trimmed) {
      if (trimmed.includes("@")) {
        toast.error("That looks like an email address, not a web link. Enter the full https:// address of your live app.");
        return;
      }
      normalizedUrl = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
      try {
        const parsed = new URL(normalizedUrl);
        if (!parsed.hostname.includes(".")) throw new Error("invalid hostname");
      } catch (e) {
        toast.error("That doesn't look like a valid web address, e.g. https://your-app.preview.emergentagent.com");
        return;
      }
    }

    setSaving(true);
    try {
      const finalStatus = normalizedUrl ? (status === "error" ? "error" : "docked") : "awaiting";
      const urlChanged = !!normalizedUrl && normalizedUrl !== (project.url || "");
      const res = await api.put(`/projects/${project.id}`, {
        url: normalizedUrl || null, api_key: apiKey || null, status: finalStatus,
        voice, expertise,
      });
      onSaved(res.data);
      onClose();
      // Only re-launch the ship when the live URL actually changed - saving a voice/expertise
      // tweak alone should never pop the app open.
      if (urlChanged) {
        toast.success(`${project.name} docked - launching now`);
        window.open(normalizedUrl, "_blank", "noopener,noreferrer");
      } else {
        toast.success(`${project.name} updated`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update ship");
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-6"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 10 }} animate={{ opacity: 1, scale: 1, y: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="glass-panel rounded-2xl p-6 w-full max-w-md max-h-[88vh] overflow-y-auto"
        data-testid="edit-project-modal"
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display font-bold text-white text-base">{project.name}</h3>
          <button data-testid="close-edit-modal" onClick={onClose} className="text-slate-500 hover:text-white transition-colors"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-[10px] font-mono-data text-slate-500 mb-1">{project.repo_name}</p>
        {project.repo_url && (
          <a
            data-testid="project-repo-link-modal"
            href={project.repo_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[10px] font-mono-data text-cyan-500/70 hover:text-cyan-400 transition-colors flex items-center gap-1 mb-4"
          >
            <GitBranch className="w-2.5 h-2.5" /> View source on GitHub
          </a>
        )}

        <label className="text-[10px] font-mono-data uppercase tracking-wider text-slate-500 block mb-1.5">Live URL (launched when this ship is clicked on the Mothership)</label>
        <input
          data-testid="project-url-input"
          value={url} onChange={(e) => setUrl(e.target.value)}
          type="url"
          autoComplete="off"
          placeholder="https://your-project.preview.emergentagent.com"
          className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-200 outline-none focus:border-cyan-400/40 mb-4 font-mono-data"
        />

        <label className="text-[10px] font-mono-data uppercase tracking-wider text-slate-500 block mb-1.5">API Key (optional)</label>
        <input
          data-testid="project-apikey-input"
          value={apiKey} onChange={(e) => setApiKey(e.target.value)}
          type="password"
          placeholder="••••••••••"
          className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-200 outline-none focus:border-cyan-400/40 mb-5 font-mono-data"
        />

        <div className="h-px bg-white/10 my-4" />
        <p className="text-[10px] font-display font-semibold uppercase tracking-wider text-violet-300/90 mb-3 flex items-center gap-1.5">
          <AudioLines className="w-3 h-3" /> Ship Voice &amp; Specialisation
        </p>

        <label className="text-[10px] font-mono-data uppercase tracking-wider text-slate-500 block mb-1.5">Voice — how this ship sounds when you speak to it live</label>
        <select
          data-testid="project-voice-select"
          value={voice}
          onChange={(e) => setVoice(e.target.value)}
          className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-200 outline-none focus:border-violet-400/40 mb-4 font-mono-data appearance-none cursor-pointer"
        >
          {VOICE_OPTIONS.map((v) => (
            <option key={v.id || "default"} value={v.id} className="bg-slate-900">{v.label}</option>
          ))}
        </select>

        <div className="flex items-center justify-between mb-1.5">
          <label className="text-[10px] font-mono-data uppercase tracking-wider text-slate-500">Domain expertise — AVA dives deep in this subject here</label>
          <button
            data-testid="suggest-expertise-button"
            onClick={handleSuggest}
            disabled={suggesting}
            className="flex items-center gap-1 text-[9px] font-display font-semibold uppercase tracking-wider text-violet-300 hover:text-violet-200 disabled:opacity-50 transition-colors"
            title="Draft a specialist profile from this ship's category & description"
          >
            {suggesting ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
            {suggesting ? "Drafting..." : "Suggest with AI"}
          </button>
        </div>
        <textarea
          data-testid="project-expertise-input"
          value={expertise}
          onChange={(e) => setExpertise(e.target.value)}
          rows={6}
          placeholder="e.g. You are the specialist voice for retinal imaging & ophthalmology. Reason in terms of OCT layers, drusen, DR/AMD grading, fundus interpretation... — or tap 'Suggest with AI' to draft one."
          className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-[11px] leading-relaxed text-slate-200 outline-none focus:border-violet-400/40 mb-1.5 resize-y"
        />
        <p className="text-[9px] font-mono-data text-slate-600 mb-5">Leave blank to keep this ship as a generalist. It still shares everything it learns with the fleet brain.</p>

        <button
          data-testid="save-project-button"
          onClick={handleSave}
          disabled={saving}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-cyan-400/15 text-cyan-300 hover:bg-cyan-400/25 border border-cyan-400/30 text-xs font-display font-semibold uppercase tracking-wider transition-colors disabled:opacity-50"
        >
          <Save className="w-3.5 h-3.5" /> {saving ? "Docking..." : "Save & Dock"}
        </button>
      </motion.div>
    </motion.div>,
    document.body
  );
}

function SuggestionsModal({ project, suggestions, onClose, onResolved }) {
  const [busyId, setBusyId] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    api.get("/suggestions", { params: { project_id: project.id } })
      .then((res) => setHistory(res.data.filter((s) => s.status !== "pending")))
      .catch(() => {});
  }, [project.id]);

  const resolve = async (suggestion, action) => {
    setBusyId(suggestion.id);
    try {
      const res = await api.post(`/suggestions/${suggestion.id}/${action}`);
      onResolved(suggestion.id);
      setHistory((prev) => [res.data, ...prev]);
      toast.success(action === "acknowledge" ? "Insight acknowledged." : "Insight dismissed.");
    } catch (e) {
      toast.error("Failed to update that insight.");
    } finally {
      setBusyId(null);
    }
  };

  return createPortal(
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-6"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 10 }} animate={{ opacity: 1, scale: 1, y: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="glass-panel rounded-2xl p-6 w-full max-w-lg"
        data-testid="suggestions-modal"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Lightbulb className="w-4 h-4 text-amber-400" />
            <h3 className="font-display font-bold text-white text-base">AVA's Insights - {project.name}</h3>
          </div>
          <button data-testid="close-suggestions-modal" onClick={onClose} className="text-slate-500 hover:text-white transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-[10px] font-mono-data text-slate-500 mb-4">
          Auto-generated as AVA's knowledge graph learns new concepts tied to this project - review and approve.
        </p>
        <div className="flex flex-col gap-3 max-h-[50vh] overflow-y-auto">
          {suggestions.length === 0 && (
            <p className="text-xs text-slate-500 text-center py-6" data-testid="no-suggestions-remaining">All caught up - no pending insights left for this project.</p>
          )}
          {suggestions.map((s) => (
            <div key={s.id} data-testid={`suggestion-item-${s.id}`} className="bg-black/30 border border-white/10 rounded-lg p-3.5">
              <p className="text-xs text-slate-200 leading-relaxed mb-2.5">{s.text}</p>
              <div className="flex flex-wrap gap-1.5 mb-3">
                {(s.concepts || []).map((c) => (
                  <span key={c} className="text-[9px] font-mono-data text-amber-300/80 bg-amber-400/10 border border-amber-400/20 rounded-full px-2 py-0.5">{c}</span>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <button
                  data-testid={`acknowledge-suggestion-${s.id}`}
                  onClick={() => resolve(s, "acknowledge")}
                  disabled={busyId === s.id}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-cyan-400/15 text-cyan-300 hover:bg-cyan-400/25 border border-cyan-400/30 text-[10px] font-mono-data uppercase tracking-wider transition-colors disabled:opacity-50"
                >
                  <Check className="w-3 h-3" /> Acknowledge
                </button>
                <button
                  data-testid={`dismiss-suggestion-${s.id}`}
                  onClick={() => resolve(s, "dismiss")}
                  disabled={busyId === s.id}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-600 text-slate-400 hover:border-slate-400 hover:text-slate-200 text-[10px] font-mono-data uppercase tracking-wider transition-colors disabled:opacity-50"
                >
                  <ThumbsDown className="w-3 h-3" /> Dismiss
                </button>
              </div>
            </div>
          ))}
        </div>

        {history.length > 0 && (
          <div className="mt-4 pt-4 border-t border-white/10">
            <button
              data-testid="toggle-suggestion-history"
              onClick={() => setShowHistory((v) => !v)}
              className="flex items-center gap-1.5 text-[10px] font-mono-data uppercase tracking-wider text-slate-500 hover:text-slate-300 transition-colors"
            >
              <History className="w-3 h-3" /> {showHistory ? "Hide" : "Show"} History ({history.length})
            </button>
            {showHistory && (
              <div className="flex flex-col gap-2 mt-3 max-h-[30vh] overflow-y-auto" data-testid="suggestion-history-list">
                {history.map((s) => (
                  <div key={s.id} data-testid={`history-item-${s.id}`} className="bg-black/20 border border-white/5 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className={`text-[9px] font-mono-data uppercase tracking-wider px-1.5 py-0.5 rounded ${s.status === "acknowledged" ? "text-cyan-300 bg-cyan-400/10" : "text-slate-500 bg-white/5"}`}>
                        {s.status}
                      </span>
                      <span className="text-[9px] font-mono-data text-slate-600">{new Date(s.created_at).toLocaleDateString()}</span>
                    </div>
                    <p className="text-[11px] text-slate-400 leading-relaxed">{s.text}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </motion.div>
    </motion.div>,
    document.body
  );
}


function CopyField({ label, value, mono = true, multiline = false, testId, rows = 8 }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    }).catch(() => toast.error("Copy failed - select and copy manually."));
  };
  return (
    <div className="mb-3">
      <div className="flex items-center justify-between mb-1">
        <p className="text-[9px] font-mono-data uppercase tracking-wider text-slate-500">{label}</p>
        <button
          onClick={copy}
          data-testid={`${testId}-copy`}
          className="flex items-center gap-1 text-[9px] font-mono-data uppercase tracking-wider text-cyan-400 hover:text-cyan-300 transition-colors"
        >
          {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      {multiline ? (
        <textarea
          readOnly
          value={value}
          data-testid={testId}
          rows={rows}
          className={`w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-[10px] text-slate-300 ${mono ? "font-mono-data" : ""} resize-y focus:outline-none focus:border-cyan-400/40`}
        />
      ) : (
        <p data-testid={testId} className={`bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-[10px] text-emerald-300 break-all ${mono ? "font-mono-data" : ""}`}>{value}</p>
      )}
    </div>
  );
}

function buildAllWiring(info) {
  const bar = "======================================================================";
  return [
    `COMPLETE AVA HIVE INTEGRATION FOR ${(info.project || "").toUpperCase()}`,
    "",
    `Hand this ENTIRE message to ${info.project}'s builder agent. It wires all three`,
    "AVA systems in a single pass: (1) the shared voice orb, (2) the fleet Hive Bus,",
    "(3) Fleet Single Sign-On. Do every part below, then redeploy this app.",
    "",
    `AVA backend: ${info.backend_url}`,
    `This ship's hive key: ${info.api_key}`,
    `Auth header for every API call below: X-AVA-Hive-Key: ${info.api_key}`,
    "",
    bar,
    "PART 1 of 3 - SHARED VOICE ORB (one line, ~2 minutes)",
    bar,
    "Add this single line just before </body> in the app's main HTML page:",
    `  ${info.script_snippet}`,
    "",
    "That renders AVA's live voice orb: the SAME AVA as the mothership - identical",
    "voice, persona and permanent cross-project memory - plus a small \"Return to AVA\"",
    "link (top-right). Nothing else is needed for voice.",
    "",
    bar,
    "PART 2 of 3 - HIVE BUS (fleet nervous system)",
    bar,
    info.bus_prompt || "",
    "",
    bar,
    "PART 3 of 3 - FLEET SINGLE SIGN-ON (Sign in with AVA / Google)",
    bar,
    info.sso_prompt || "",
  ].join("\n");
}

function HiveModal({ project, onClose }) {
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/projects/${project.id}/hive`)
      .then((res) => setInfo(res.data))
      .catch(() => setError("Couldn't load hive credentials - try again."));
  }, [project.id]);

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose} data-testid="hive-modal-overlay">
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl max-h-[85vh] overflow-y-auto bg-[#070b12] border border-emerald-400/25 rounded-2xl p-6 shadow-[0_0_60px_rgba(16,185,129,0.15)]"
        data-testid="hive-modal"
      >
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-display font-bold text-white text-base flex items-center gap-2">
            <BrainCircuit className="w-4 h-4 text-emerald-400" /> Hive Mind - {project.name}
          </h3>
          <button data-testid="close-hive-modal" onClick={onClose} className="text-slate-500 hover:text-white transition-colors"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-[11px] text-slate-500 mb-4">
          One mind, one voice: plug this ship into AVA's shared brain. Hand the builder instructions below to this project's own builder agent - it's a one-time, ~2 minute integration.
        </p>
        {error && <p className="text-xs text-red-400" data-testid="hive-modal-error">{error}</p>}
        {!info && !error && <p className="text-xs text-slate-500 animate-pulse">Minting hive credentials...</p>}
        {info && (
          <>
            <div className="mb-4 rounded-xl border border-cyan-400/30 bg-cyan-400/[0.05] p-3 shadow-[0_0_30px_rgba(34,211,238,0.08)]" data-testid="hive-all-wiring-bundle">
              <div className="flex items-center gap-2 mb-2">
                <span className="px-1.5 py-0.5 rounded bg-cyan-400/20 text-cyan-200 text-[8px] font-mono-data uppercase tracking-wider border border-cyan-400/30">1-Click</span>
                <p className="text-[11px] font-display font-semibold text-cyan-200">Copy ALL Wiring - one paste, whole ship</p>
              </div>
              <p className="text-[10px] text-slate-500 mb-2 leading-relaxed">
                The complete bundle: Voice Orb + Hive Bus + Fleet SSO in a single payload. Copy this one field, paste it into this ship's builder agent, and it wires all three at once.
              </p>
              <CopyField label="Complete AVA integration (Orb + Bus + SSO)" value={buildAllWiring(info)} multiline testId="hive-all-wiring" rows={10} />
            </div>
            <CopyField label="Hive API key" value={info.api_key} testId="hive-api-key" />
            <CopyField label="Shared Voice Orb - one line before </body>" value={info.script_snippet} testId="hive-script-snippet" />
            <CopyField label="Full builder instructions (paste to this project's builder agent)" value={info.instructions} multiline testId="hive-instructions" />
            <CopyField label="Hive Bus wiring - fleet nervous system (paste to this project's builder agent)" value={info.bus_prompt} multiline testId="hive-bus-prompt" />
            <CopyField label="Fleet SSO - Sign in with AVA / Google (paste to this project's builder agent)" value={info.sso_prompt} multiline testId="hive-sso-prompt" />
            <div className="mt-4 rounded-xl border border-emerald-400/30 bg-emerald-400/[0.05] p-3 shadow-[0_0_30px_rgba(16,185,129,0.08)]" data-testid="hive-local-context-box">
              <div className="flex items-center gap-2 mb-2">
                <span className="px-1.5 py-0.5 rounded bg-emerald-400/20 text-emerald-200 text-[8px] font-mono-data uppercase tracking-wider border border-emerald-400/30">Optional</span>
                <p className="text-[11px] font-display font-semibold text-emerald-200">Local Context Bridge - make this ship's OWN screens remember (Retina-style)</p>
              </div>
              <p className="text-[10px] text-slate-500 mb-2 leading-relaxed">
                AVA already remembers every ship's conversations in the shared brain. This extra step lets AVA read + write this app's OWN internal data (people & conversations), so its own UI reflects what she hears - like Retina's "last learned: Layla · via voice". Paste into this ship's builder, then redeploy.
              </p>
              <CopyField label="Local context bridge (ABSORB + RETAIN + remember_local)" value={info.local_context_prompt} multiline testId="hive-local-context-prompt" rows={10} />
            </div>
            <p className="text-[10px] text-slate-600 mt-1">
              Endpoints: <span className="font-mono-data text-slate-400">POST /api/hive/context</span> (pull AVA's knowledge) · <span className="font-mono-data text-slate-400">POST /api/hive/learn</span> (teach AVA) · <span className="font-mono-data text-slate-400">POST /api/hive/person</span> (shared patient/person dossier) · orb.js (shared multilingual voice). Auth header: <span className="font-mono-data text-slate-400">X-AVA-Hive-Key</span>.
            </p>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

function timeAgo(iso) {
  if (!iso) return "never";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

// One row per ship: is its hive key minted, is AVA's orb actually running on the live
// site, when did it last talk to the hive, and how much knowledge has it synced back.
const MIG_STEPS = [
  { key: "github", label: "GitHub saved" },
  { key: "decoupled", label: "Deps decoupled" },
  { key: "deployed", label: "Deployed" },
  { key: "dns", label: "DNS live" },
  { key: "hive_repoint", label: "Hive re-pointed" },
  { key: "shutdown", label: "Emergent off" },
];

function HiveStatusPanel({ projects, onOpenHive }) {
  const [rows, setRows] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [expandedMig, setExpandedMig] = useState(null);
  const [evacuating, setEvacuating] = useState(null);

  const load = () => api.get("/projects/hive-status").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const scan = async () => {
    setScanning(true);
    try {
      const res = await api.post("/projects/hive-status/scan");
      await load();
      toast.success(`Fleet scanned - orb embed found on ${res.data.orb_found}/${res.data.scanned} live ships.`);
    } catch {
      toast.error("Fleet scan failed.");
    } finally {
      setScanning(false);
    }
  };

  const orbState = (r) => {
    if (r.orb_last_loaded && Date.now() - new Date(r.orb_last_loaded).getTime() < 7 * 86400000) return "live";
    if (r.orb_embedded) return "embedded";
    return "none";
  };
  const isConnected = (r) => orbState(r) !== "none" || !!r.last_hive_contact;
  const sorted = [...rows].sort((a, b) => Number(isConnected(a)) - Number(isConnected(b)) || a.name.localeCompare(b.name));
  const connectedCount = rows.filter(isConnected).length;

  const copyDoctrine = async (r, project) => {
    try {
      const res = await api.get(`/projects/${r.id}/hive`);
      await navigator.clipboard.writeText(res.data.instructions);
      toast.success(`Doctrine sheet for ${r.name} copied - paste it into that project's builder.`);
    } catch {
      toast.error("Couldn't copy - opening the sheet instead.");
    }
    if (project) onOpenHive(project);
  };

  const migDone = (r) => MIG_STEPS.filter((s) => r.migration?.[s.key]).length;

  const toggleStep = async (r, step) => {
    const done = !r.migration?.[step];
    try {
      const res = await api.put(`/projects/${r.id}/migration`, { step, done });
      setRows((prev) => prev.map((x) => (x.id === r.id ? { ...x, migration: res.data.migration } : x)));
    } catch {
      toast.error("Couldn't update the migration step.");
    }
  };

  const evacuate = async (path, filename, label) => {
    setEvacuating(label);
    try {
      const res = await api.get(path, { responseType: "blob", timeout: 300000 });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${label} downloaded - keep it somewhere safe.`);
    } catch {
      toast.error(`${label} download failed.`);
    } finally {
      setEvacuating(null);
    }
  };

  return (
    <div className="mb-6 rounded-xl border border-emerald-400/15 bg-emerald-400/[0.03] p-4" data-testid="hive-status-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <BrainCircuit className="w-4 h-4 text-emerald-300" />
          <h3 className="font-display font-semibold text-white text-sm">Hive Mind Integration</h3>
          <span className="text-[10px] font-mono-data text-emerald-300/80" data-testid="hive-connected-count">
            {connectedCount}/{rows.length} ships connected
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="evacuate-storage-button"
            onClick={() => evacuate("/export/files", "octon_storage_evacuation.zip", "Storage evacuation")}
            disabled={!!evacuating}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-400/10 hover:bg-amber-400/20 border border-amber-400/25 text-amber-300 text-[10px] font-mono-data uppercase tracking-wider transition-colors disabled:opacity-50"
            title="Download every file still in Emergent Object Storage as one zip - do this before shutdown"
          >
            <Download className="w-3 h-3" /> {evacuating === "Storage evacuation" ? "Evacuating..." : "Evacuate Storage"}
          </button>
          <button
            data-testid="download-brain-button"
            onClick={() => evacuate("/export/brain", "octon_brain_export.jsonl.gz", "Brain export")}
            disabled={!!evacuating}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-cyan-400/10 hover:bg-cyan-400/20 border border-cyan-400/25 text-cyan-300 text-[10px] font-mono-data uppercase tracking-wider transition-colors disabled:opacity-50"
            title="Full brain export (every collection) for import on your own server"
          >
            <Download className="w-3 h-3" /> {evacuating === "Brain export" ? "Exporting..." : "Download Brain"}
          </button>
          <button
            data-testid="hive-scan-fleet-button"
            onClick={scan}
            disabled={scanning}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-400/10 hover:bg-emerald-400/20 border border-emerald-400/25 text-emerald-300 text-[10px] font-mono-data uppercase tracking-wider transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-3 h-3 ${scanning ? "animate-spin" : ""}`} /> {scanning ? "Scanning..." : "Scan Fleet"}
          </button>
        </div>
      </div>
      <div className="space-y-1" data-testid="hive-status-rows">
        {sorted.map((r) => {
          const orb = orbState(r);
          const project = projects.find((p) => p.id === r.id);
          return (
            <React.Fragment key={r.id}>
            <div
              data-testid={`hive-status-row-${r.id}`}
              className="grid grid-cols-12 items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-white/[0.03] transition-colors"
            >
              <span className="col-span-3 flex items-center gap-1.5 min-w-0">
                <span className="text-[11px] text-slate-200 truncate">{r.name}</span>
                <span data-testid={`independence-badge-${r.id}`} className={`shrink-0 px-1 py-0.5 rounded border text-[7px] font-mono-data uppercase tracking-wider ${!r.url ? "border-white/10 text-slate-600" : r.url.includes("emergentagent.com") ? "border-amber-400/25 text-amber-300/90 bg-amber-400/10" : "border-emerald-400/30 text-emerald-300 bg-emerald-400/10"}`}>
                  {!r.url ? "no url" : r.url.includes("emergentagent.com") ? "emergent" : "own domain"}
                </span>
                {r.url && !r.url.includes("emergentagent.com") && (r.down_since || r.health?.status === "down") && (
                  <span data-testid={`dark-badge-${r.id}`} className="shrink-0 px-1 py-0.5 rounded border border-rose-500/40 bg-rose-500/15 text-rose-300 text-[7px] font-mono-data uppercase tracking-wider animate-pulse">
                    dark
                  </span>
                )}
                <button
                  data-testid={`migration-toggle-${r.id}`}
                  onClick={() => setExpandedMig(expandedMig === r.id ? null : r.id)}
                  className={`shrink-0 px-1 py-0.5 rounded border text-[7px] font-mono-data uppercase tracking-wider transition-colors ${migDone(r) === MIG_STEPS.length ? "border-emerald-400/30 text-emerald-300 bg-emerald-400/10" : "border-cyan-400/25 text-cyan-300/80 hover:bg-cyan-400/10"}`}
                  title="Migration Command Center - tick off this ship's independence steps"
                >
                  mig {migDone(r)}/{MIG_STEPS.length}
                </button>
              </span>
              <span className={`col-span-1 text-[9px] font-mono-data uppercase ${r.has_key ? "text-emerald-400" : "text-rose-400"}`}>
                {r.has_key ? "key ✓" : "no key"}
              </span>
              <span className={`col-span-2 text-[9px] font-mono-data uppercase ${orb === "live" ? "text-emerald-400" : orb === "embedded" ? "text-cyan-300" : "text-slate-500"}`}>
                {orb === "live" ? "● orb live" : orb === "embedded" ? "orb embedded" : "orb not found"}
              </span>
              <span className={`col-span-2 text-[9px] font-mono-data ${r.last_hive_contact ? "text-cyan-200" : "text-slate-600"}`}>
                contact: {timeAgo(r.last_hive_contact)}
              </span>
              <span className="col-span-2 text-[9px] font-mono-data text-slate-400">
                mem {r.memories} · rec {r.records}
              </span>
              <span className="col-span-2 flex justify-end">
                {project && (
                  <button
                    data-testid={`hive-status-instructions-${r.id}`}
                    onClick={() => copyDoctrine(r, project)}
                    className={`flex items-center gap-1 px-2 py-1 rounded text-[9px] font-mono-data uppercase tracking-wider transition-colors ${
                      isConnected(r)
                        ? "text-slate-400 hover:text-cyan-300 bg-white/[0.03] hover:bg-cyan-400/10"
                        : "text-amber-300 bg-amber-400/10 hover:bg-amber-400/20 border border-amber-400/25"
                    }`}
                  >
                    <Copy className="w-2.5 h-2.5" /> {isConnected(r) ? "Copy doctrine" : "Not integrated - copy"}
                  </button>
                )}
              </span>
            </div>
            {expandedMig === r.id && (
              <div data-testid={`migration-checklist-${r.id}`} className="flex flex-wrap items-center gap-1.5 px-2 pb-2 ml-5">
                <span className="text-[8px] font-mono-data uppercase tracking-wider text-slate-600 mr-1">independence:</span>
                {MIG_STEPS.map((s) => {
                  const done = !!r.migration?.[s.key];
                  return (
                    <button
                      key={s.key}
                      data-testid={`migration-step-${s.key}-${r.id}`}
                      onClick={() => toggleStep(r, s.key)}
                      className={`flex items-center gap-1 px-2 py-1 rounded border text-[9px] font-mono-data transition-colors ${done ? "border-emerald-400/30 bg-emerald-400/10 text-emerald-300" : "border-white/10 bg-white/[0.02] text-slate-500 hover:text-slate-300 hover:border-white/25"}`}
                    >
                      {done ? <CheckSquare className="w-2.5 h-2.5" /> : <Square className="w-2.5 h-2.5" />} {s.label}
                    </button>
                  );
                })}
              </div>
            )}
            </React.Fragment>
          );
        })}
      </div>
      <p className="mt-2 text-[9px] text-slate-500">
        ● orb live = a page loaded AVA's orb script in the last 7 days · orb embedded = found in the site's HTML · contact = last authenticated hive API call from that ship. To connect a ship, copy its instructions into that project's builder.
      </p>
    </div>
  );
}

export default function DockingBay({ focusProjectId, onFocusHandled }) {
  const [projects, setProjects] = useState([]);
  const [editing, setEditing] = useState(null);
  const [syncingAll, setSyncingAll] = useState(false);
  const [syncingId, setSyncingId] = useState(null);
  const [githubReady, setGithubReady] = useState(true);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsProject, setSuggestionsProject] = useState(null);
  const [hiveProject, setHiveProject] = useState(null);
  const [bridgeHealth, setBridgeHealth] = useState({});

  const fetchProjects = () => api.get("/projects").then((res) => setProjects(res.data));
  const fetchSuggestions = () => api.get("/suggestions", { params: { status: "pending" } }).then((res) => setSuggestions(res.data)).catch(() => {});
  const fetchBridgeHealth = () => api.get("/projects/bridge-health").then((res) => setBridgeHealth(res.data || {})).catch(() => {});

  useEffect(() => {
    fetchProjects();
    fetchSuggestions();
    fetchBridgeHealth();
    handleSyncAll(true);
  }, []);

  useEffect(() => {
    if (focusProjectId && projects.length > 0) {
      const target = projects.find((p) => p.id === focusProjectId);
      if (target) {
        setEditing(target);
        onFocusHandled && onFocusHandled();
      }
    }
  }, [focusProjectId, projects, onFocusHandled]);

  const handleSaved = (updated) => setProjects((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));

  const handleSyncAll = async (silent = false) => {
    setSyncingAll(true);
    try {
      const res = await api.post("/projects/sync");
      const { new_repos, synced, github_configured: ghOk } = res.data;
      setGithubReady(ghOk);
      await fetchProjects();
      if (!silent) {
        if (!ghOk) {
          toast.error("GITHUB_TOKEN has no repository access yet - brain sync is idle.");
        } else {
          toast.success(`Fleet synced: ${new_repos.length} new vessel(s) discovered, ${synced.length} brain-updated.`);
        }
      }
    } catch (e) {
      if (!silent) toast.error("Fleet sync failed.");
    } finally {
      setSyncingAll(false);
    }
  };

  const handleSyncOne = async (project) => {
    setSyncingId(project.id);
    try {
      const res = await api.post(`/projects/${project.id}/sync`);
      if (res.data.status === "synced") {
        toast.success(`${project.name} fed ${res.data.concepts?.length || 0} new concepts into AVA's brain.`);
      } else if (res.data.status === "empty") {
        toast.info(`${project.name} has no commits yet - repo is under construction on GitHub.`);
      } else if (res.data.status === "no_access") {
        toast.error(`No git access to ${project.name} yet - check GITHUB_TOKEN permissions.`);
      } else if (res.data.status === "up_to_date") {
        toast.info(`${project.name} already reflects AVA's latest memory.`);
      }
      await fetchProjects();
    } catch (e) {
      toast.error("Sync failed.");
    } finally {
      setSyncingId(null);
    }
  };

  const dockedCount = projects.filter((p) => p.status === "docked").length;
  const berthsUsed = projects.length;
  const spareBerths = Math.max(0, FLEET_CAPACITY - berthsUsed);
  const capacityPct = Math.min(100, Math.round((berthsUsed / FLEET_CAPACITY) * 100));
  const capacityTone = capacityPct >= 100 ? "rose" : capacityPct >= 85 ? "amber" : "emerald";

  return (
    <div className="col-span-1 md:col-span-10 h-full glass-panel rounded-2xl p-4 sm:p-6 overflow-y-auto" data-testid="docking-bay">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display font-bold text-white text-lg">Docking Bay</h2>
          <p className="text-[11px] text-slate-500 mt-1">{dockedCount} / {projects.length} vessels docked to the mothership</p>
          {!githubReady && (
            <p className="text-[10px] font-mono-data text-amber-400 mt-1" data-testid="github-token-warning">
              GITHUB_TOKEN present but lacks repository permissions - grant Contents: Read-only to enable live brain sync.
            </p>
          )}
        </div>
        <div className="flex items-center gap-4">
          {/* Berth capacity gauge - berths used vs spare, colour-shifting as the carrier fills. */}
          <div className="hidden sm:flex flex-col items-end gap-1" data-testid="berth-capacity-gauge">
            <span className="text-[10px] font-mono-data uppercase tracking-wider text-slate-400">
              Berths{" "}
              <span className={capacityTone === "rose" ? "text-rose-300" : capacityTone === "amber" ? "text-amber-300" : "text-emerald-300"} data-testid="berth-capacity-count">
                {berthsUsed}/{FLEET_CAPACITY}
              </span>
              {" "}· <span data-testid="berth-spare-count">{spareBerths} spare</span>
              {capacityPct >= 100 && <span className="text-rose-400 ml-1">· BAY FULL</span>}
            </span>
            <div className="w-44 h-1.5 rounded-full bg-white/10 overflow-hidden">
              <div
                data-testid="berth-capacity-bar"
                className={`h-full rounded-full transition-[width] duration-700 ${capacityTone === "rose" ? "bg-rose-400" : capacityTone === "amber" ? "bg-amber-400" : "bg-emerald-400"}`}
                style={{ width: `${capacityPct}%` }}
              />
            </div>
          </div>
          <button
            data-testid="sync-fleet-button"
            onClick={() => handleSyncAll(false)}
            disabled={syncingAll}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-400/10 hover:bg-cyan-400/20 border border-cyan-400/25 text-cyan-300 text-[11px] font-mono-data uppercase tracking-wider transition-colors disabled:opacity-50"
          >
            <GitBranch className={`w-3.5 h-3.5 ${syncingAll ? "animate-spin" : ""}`} /> {syncingAll ? "Breathing In..." : "Sync Fleet Now"}
          </button>
        </div>
      </div>
      <HiveStatusPanel projects={projects} onOpenHive={setHiveProject} />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {projects.map((p) => (
          <ProjectCard
            key={p.id}
            project={p}
            onEdit={setEditing}
            onSync={handleSyncOne}
            syncing={syncingId === p.id}
            suggestions={suggestions.filter((s) => s.project_id === p.id)}
            onOpenSuggestions={setSuggestionsProject}
            onOpenHive={setHiveProject}
            bridge={bridgeHealth[p.id]}
          />
        ))}
      </div>
      {editing && <EditModal project={editing} onClose={() => setEditing(null)} onSaved={handleSaved} />}
      {hiveProject && <HiveModal project={hiveProject} onClose={() => setHiveProject(null)} />}
      {suggestionsProject && (
        <SuggestionsModal
          project={suggestionsProject}
          suggestions={suggestions.filter((s) => s.project_id === suggestionsProject.id)}
          onClose={() => setSuggestionsProject(null)}
          onResolved={(id) => setSuggestions((prev) => prev.filter((s) => s.id !== id))}
        />
      )}
    </div>
  );
}
